import React, { useState, useEffect } from 'react';
import { Table, Card, Button, Badge, Row, Col, Form, InputGroup, Dropdown, Alert } from 'react-bootstrap';
import { Search, Filter, Download, Plus, EyeIcon, CreditCard } from 'lucide-react';
import { Link, useNavigate } from 'react-router-dom';
import useAuth from '../../hooks/useAuth';
import GenererPdfFacture from './genererPdfFacture';
import { createRoot } from 'react-dom/client';

import {
    // Méthodes pour Admin/Manager/Sales
    getAllInvoices,
    getInvoicesByStatus,
    getOverdueInvoices,
    getInvoiceById,
    recordPayment,
    // Nouvelles méthodes pour Distributeur
    getMyInvoices,
    getMyInvoicesByStatus,
    getMyOverdueInvoices
} from '../../api/invoices';
import { formatCurrency, formatDate } from '../../utils/formatters';
import { getDeliveryNoteById } from '../../api/orders';
import Loading from '../common/Loading';

const InvoiceList = ({ onViewInvoice, onCreateInvoice, onPreviewInvoice, companyInfo }) => {
    const { isAdmin, isManager, isSalesRep, isDistributor, user } = useAuth();

    const [invoices, setInvoices] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [success, setSuccess] = useState('');
    const [filter, setFilter] = useState('all');
    const [searchTerm, setSearchTerm] = useState('');

    const navigate = useNavigate();

    // Montant TTC = (HT − remise) + TVA éventuelle (selon la commande)
    const computeInvoiceTTC = (invoice) => {
        const net = (invoice.totalAmount || 0) - (invoice.discountAmount || 0);
        return invoice.order?.vatApplicable ? net * 1.18 : net;
    };

    useEffect(() => {
        fetchInvoices();
    }, [filter]);

    const fetchInvoices = async () => {
        setLoading(true);
        setError('');

        try {
            let data;

            if (isDistributor) {
                // 🎯 Logique pour les distributeurs
                switch (filter) {
                    case 'pending':
                        data = await getMyInvoicesByStatus('PENDING');
                        break;
                    case 'paid':
                        data = await getMyInvoicesByStatus('PAID');
                        break;
                    case 'overdue':
                        data = await getMyOverdueInvoices();
                        break;
                    case 'cancelled':
                        data = await getMyInvoicesByStatus('CANCELLED');
                        break;
                    default:
                        data = await getMyInvoices();
                }
            } else {
                // 🎯 Logique pour Admin/Manager/Sales
                switch (filter) {
                    case 'pending':
                        data = await getInvoicesByStatus('PENDING');
                        break;
                    case 'paid':
                        data = await getInvoicesByStatus('PAID');
                        break;
                    case 'overdue':
                        data = await getOverdueInvoices();
                        break;
                    case 'cancelled':
                        data = await getInvoicesByStatus('CANCELLED');
                        break;
                    default:
                        data = await getAllInvoices();
                }
            }

            // 🧠 Trie par date décroissante (plus récentes en haut)
            const sortedData = (Array.isArray(data) ? data : []).sort((a, b) => b.id - a.id);

            setInvoices(sortedData);
        } catch (err) {
            setError('Impossible de charger les factures. Veuillez réessayer.');
            console.error(err);
        } finally {
            setLoading(false);
        }
    };

    const handlePaymentRecording = async (invoiceId) => {
        // Vérifier si l'utilisateur a les droits pour enregistrer des paiements
        if (isDistributor) {
            setError('Vous n\'avez pas les droits pour enregistrer des paiements.');
            setTimeout(() => setError(''), 3000);
            return;
        }

        // Demander confirmation avant d'enregistrer le paiement
        const confirmed = window.confirm('Êtes-vous sûr de vouloir enregistrer ce paiement ?');

        if (!confirmed) {
            return; // Annuler l'opération si l'utilisateur clique sur "Annuler"
        }

        try {
            await recordPayment(invoiceId);

            // Mettre à jour le statut de la facture dans la liste locale
            setInvoices(prevInvoices =>
                prevInvoices.map(invoice =>
                    invoice.id === invoiceId
                        ? { ...invoice, status: 'PAID', paidDate: new Date().toISOString() }
                        : invoice
                )
            );

            setSuccess('Paiement enregistré avec succès !');

            // Effacer le message de succès après 3 secondes
            setTimeout(() => setSuccess(''), 3000);
        } catch (err) {
            setError(err.message || 'Erreur lors de l\'enregistrement du paiement');

            // Effacer le message d'erreur après 3 secondes
            setTimeout(() => setError(''), 3000);
        }
    };

    // Nouvelle fonction pour télécharger le PDF
    const handleDownloadInvoice = async (invoiceId) => {
        try {
            setLoading(true);
            const invoiceData = await getInvoiceById(invoiceId);

            // 🔍 Log des données de la facture
            console.log("Invoice data récupérée :", invoiceData);

            // Charger le bon de livraison facturé pour ses lignes
            let deliveryNoteData = null;
            if (invoiceData.deliveryNoteId) {
                try {
                    deliveryNoteData = await getDeliveryNoteById(invoiceData.deliveryNoteId);
                } catch (e) {
                    console.error("BL introuvable pour la facture", e);
                }
            }

            const pdfElement = document.createElement('div');
            pdfElement.style.position = 'absolute';
            pdfElement.style.left = '-9999px';
            document.body.appendChild(pdfElement);

            const root = createRoot(pdfElement);
            root.render(
                <GenererPdfFacture
                    invoice={invoiceData}
                    company={companyInfo}
                    deliveryNote={deliveryNoteData}
                    onDownload={(blob) => {
                        try {
                            if (!(blob instanceof Blob)) {
                                console.error('Error: Not a valid Blob object', blob);
                                setLoading(false);
                                return;
                            }

                            const url = URL.createObjectURL(blob);
                            const link = document.createElement('a');
                            link.href = url;
                            link.download = `facture_${invoiceData.invoiceNumber}.pdf`;
                            document.body.appendChild(link);
                            link.click();

                            document.body.removeChild(link);
                            URL.revokeObjectURL(url);
                            document.body.removeChild(pdfElement);
                            root.unmount();
                        } catch (error) {
                            console.error('Error downloading invoice:', error);
                        } finally {
                            setLoading(false);
                        }
                    }}
                />
            );
        } catch (err) {
            console.error('Erreur lors du téléchargement de la facture:', err);
            setError('Impossible de télécharger la facture');
            setLoading(false);
        }
    };

    const getStatusBadge = (status) => {
        switch (status) {
            case 'PENDING':
                return <Badge bg="warning">En attente</Badge>;
            case 'PAID':
                return <Badge bg="success">Payée</Badge>;
            case 'OVERDUE':
                return <Badge bg="danger">En retard</Badge>;
            case 'CANCELLED':
                return <Badge bg="secondary">Annulée</Badge>;
            default:
                return <Badge bg="info">{status}</Badge>;
        }
    };

    const filteredInvoices = invoices.filter(invoice =>
        invoice.invoiceNumber.toLowerCase().includes(searchTerm.toLowerCase()) ||
        invoice.client.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
        (invoice.order.orderNumber && invoice.order.orderNumber.toLowerCase().includes(searchTerm.toLowerCase())) || 
        (invoice.deliveryNoteReference && invoice.deliveryNoteReference.toLowerCase().includes(searchTerm.toLowerCase()))
    );

    // Actions réutilisées (tableau lg + cartes mobile)
    const renderActions = (invoice) => (
        <div className="d-flex gap-2 flex-wrap justify-content-end">
            <Button
                variant="outline-info"
                size="sm"
                onClick={() => onViewInvoice(invoice.id)}
                title="Voir les détails"
            >
                <EyeIcon size={16} />
            </Button>
            <Button
                variant="outline-primary"
                size="sm"
                onClick={() => handleDownloadInvoice(invoice.id)}
                title="Télécharger la facture"
            >
                <Download size={16} />
            </Button>
            {/* Le bouton paiement n'est affiché que pour Admin/Sales */}
            {!isDistributor && invoice.status === 'PENDING' && (
                <Button
                    variant="outline-success"
                    size="sm"
                    onClick={() => handlePaymentRecording(invoice.id)}
                    title="Enregistrer le paiement"
                >
                    <CreditCard size={16} />
                </Button>
            )}
        </div>
    );

    return (
        <Card className="shadow-sm">
            <Card.Header>
                <Row className="align-items-center g-2">
                    <Col xs={12} sm>
                        <h5 className="mb-0">
                            {isDistributor ? 'Mes Factures' : 'Gestion des Factures'}
                        </h5>
                    </Col>
                    <Col xs={12} sm="auto">
                        {/* Le bouton "Nouvelle Facture" n'est affiché que pour Admin/Sales */}
                        {!isDistributor && (
                            <Button
                                variant="primary"
                                size="sm"
                                onClick={onCreateInvoice}
                            >
                                <Plus size={16} className="me-1" />
                                Nouvelle Facture
                            </Button>
                        )}
                    </Col>
                </Row>
            </Card.Header>
            <Card.Body>
                {error && <Alert variant="danger">{error}</Alert>}
                {success && <Alert variant="success">{success}</Alert>}

                <Row className="mb-3 g-2">
                    <Col xs={12} md={6} lg={4}>
                        <InputGroup>
                            <InputGroup.Text>
                                <Search size={18} />
                            </InputGroup.Text>
                            <Form.Control
                                placeholder={isDistributor ? "Rechercher dans mes factures..." : "Rechercher une facture..."}
                                value={searchTerm}
                                onChange={(e) => setSearchTerm(e.target.value)}
                            />
                        </InputGroup>
                    </Col>
                    <Col xs="auto">
                        <Dropdown>
                            <Dropdown.Toggle variant="outline-secondary" id="filter-dropdown">
                                <Filter size={18} className="me-1" />
                                {filter === 'all' && 'Toutes les factures'}
                                {filter === 'pending' && 'En attente'}
                                {filter === 'paid' && 'Payées'}
                                {filter === 'overdue' && 'En retard'}
                                {filter === 'cancelled' && 'Annulées'}
                            </Dropdown.Toggle>
                            <Dropdown.Menu>
                                <Dropdown.Item onClick={() => setFilter('all')}>Toutes les factures</Dropdown.Item>
                                <Dropdown.Item onClick={() => setFilter('pending')}>En attente</Dropdown.Item>
                                <Dropdown.Item onClick={() => setFilter('paid')}>Payées</Dropdown.Item>
                                <Dropdown.Item onClick={() => setFilter('overdue')}>En retard</Dropdown.Item>
                                <Dropdown.Item onClick={() => setFilter('cancelled')}>Annulées</Dropdown.Item>
                            </Dropdown.Menu>
                        </Dropdown>
                    </Col>
                    <Col xs="auto">
                        <Button
                            variant="outline-secondary"
                            onClick={() => fetchInvoices()}
                        >
                            Actualiser
                        </Button>
                    </Col>
                </Row>

                {loading ? (
                    <Loading />
                ) : filteredInvoices.length === 0 ? (
                    <Alert variant="info">
                        {isDistributor
                            ? "Aucune facture trouvée dans votre compte."
                            : "Aucune facture trouvée pour les critères sélectionnés."
                        }
                    </Alert>
                ) : (
                    <>
                        {/* ≥ lg : tableau */}
                        <div className="table-responsive d-none d-lg-block">
                            <Table striped hover className="mb-0">
                                <thead>
                                    <tr>
                                        <th>N° Facture</th>
                                        {!isDistributor && <th>Client</th>}
                                        <th>N° BL</th>
                                        <th>Date d'émission</th>
                                        <th>Date d'échéance</th>
                                        <th>Montant TTC</th>
                                        <th>Status</th>
                                        <th className="text-end">Actions</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {filteredInvoices.map((invoice) => (
                                        <tr key={invoice.id}>
                                            <td>{invoice.invoiceNumber}</td>
                                            {!isDistributor && <td>{invoice.client.name}</td>}
                                            <td>{invoice.deliveryNoteReference || '—'}</td>
                                            <td>{formatDate(invoice.issueDate)}</td>
                                            <td>{formatDate(invoice.dueDate)}</td>
                                            <td>{formatCurrency(computeInvoiceTTC(invoice))}</td>
                                            <td>{getStatusBadge(invoice.status)}</td>
                                            <td>{renderActions(invoice)}</td>
                                        </tr>
                                    ))}
                                </tbody>
                            </Table>
                        </div>

                        {/* < lg : cartes */}
                        <div className="d-lg-none">
                            {filteredInvoices.map((invoice) => (
                                <Card key={invoice.id} className="mb-2 shadow-sm">
                                    <Card.Body className="p-3">
                                        <div className="d-flex justify-content-between align-items-start mb-2">
                                            <div>
                                                <div className="fw-bold">{invoice.invoiceNumber}</div>
                                                <small className="text-muted">
                                                    BL : {invoice.deliveryNoteReference || '—'}
                                                </small>
                                            </div>
                                            {getStatusBadge(invoice.status)}
                                        </div>

                                        {!isDistributor && (
                                            <div className="small mb-1">
                                                <span className="text-muted">Client : </span>
                                                {invoice.client.name}
                                            </div>
                                        )}

                                        <div className="small mb-1">
                                            <span className="text-muted">Émission : </span>
                                            {formatDate(invoice.issueDate)}
                                            <span className="text-muted ms-2">Échéance : </span>
                                            {formatDate(invoice.dueDate)}
                                        </div>

                                        <div className="mb-3">
                                            <span className="text-muted small">Montant TTC : </span>
                                            <span className="fw-semibold">{formatCurrency(computeInvoiceTTC(invoice))}</span>
                                        </div>

                                        {renderActions(invoice)}
                                    </Card.Body>
                                </Card>
                            ))}
                        </div>
                    </>
                )}
            </Card.Body>
        </Card>
    );
};

export default InvoiceList;